import {action, computed, makeObservable, observable} from 'mobx';

import {removeFile} from '../../utils/s3/removeFile';
import {FileProvider} from '../../utils/file/FileProvider';
import {IStorageService} from '../../platform/services/storage/StorageService';


export interface FileUploadsOptions<T> {
    uploadFn: (file: File) => Promise<T>;
    onError?: (err: Error) => void;
    initialMetas?: T[];
    getPreviewUrl?: (meta: T) => Promise<string>;
    storageService: IStorageService;
    fileProvider: FileProvider;
    tenantId: string;
}

export class FileUploadEntry<T> {
    file?: File; /* temp file in browser */
    meta?: T;
    previewUrl?: string;
    isExisting: boolean;
    isUploading: boolean;

    constructor(params: {file?: File; meta?: T; previewUrl?: string; isExisting: boolean; isUploading?: boolean}) {
        this.file = params.file;
        this.meta = params.meta;
        this.previewUrl = params.previewUrl;
        this.isExisting = params.isExisting;
        this.isUploading = params.isUploading ?? false;

        makeObservable(this, {
            file: observable.ref,
            meta: observable.ref,
            previewUrl: observable,
            isExisting: observable,
            isUploading: observable,
            setMeta: action,
            setPreviewUrl: action,
            setUploading: action,
        });
    }

    setMeta(meta: T) {
        this.meta = meta;
    }

    setPreviewUrl(url: string) {
        this.previewUrl = url;
    }

    setUploading(val: boolean) {
        this.isUploading = val;
    }
}

export class FileUploadsManager<T> {
    entries: FileUploadEntry<T>[] = [];
    private promises: Promise<void>[] = [];
    private uploadFn: (file: File) => Promise<T>;
    private onError?: (err: Error) => void;
    private getPreviewUrl: (meta: T) => Promise<string>;
    private storageService: IStorageService;
    private fileProvider: FileProvider;
    private tenantId: string;

    constructor(options: FileUploadsOptions<T>) {
        this.uploadFn = options.uploadFn;
        this.onError = options.onError;
        this.storageService = options.storageService;
        this.fileProvider = options.fileProvider;
        this.tenantId = options.tenantId;
        this.getPreviewUrl = options.getPreviewUrl || (async (meta: any) => meta?.path ? this.fileProvider.getSignedUrl(meta.path) : '');

        makeObservable(this, {
            entries: observable.shallow,
            addFiles: action.bound,
            removeFileAt: action.bound,
            clear: action.bound,
            uploading: computed,
            json: computed,
            previewURLs: computed,
        });

        if (options.initialMetas) {
            for (const meta of options.initialMetas) {
                const entry = new FileUploadEntry<T>({meta, isExisting: true});
                this.entries.push(entry);
            }
        }
    }

    get uploading() {
        return this.promises.length > 0;
    }

    get json() {
        return this.entries.map(entry => entry.meta!).filter(meta => !!meta);
    }
    get previewURLs() {
        return this.entries.map(entry => entry.previewUrl);
    }

    addFiles(files: File[] | FileList) {
        const fileArray = Array.from(files);
        for (const file of fileArray) {
            const previewUrl = URL.createObjectURL(file);
            const entry = new FileUploadEntry<T>({file, previewUrl, isExisting: false, isUploading: true});
            this.entries.push(entry);
            const promise = this.uploadFn(file)
                .then(async meta => {
                    entry.setMeta(meta);
                    const url = await this.getPreviewUrl(meta);
                    URL.revokeObjectURL(previewUrl);
                    entry.setPreviewUrl(url);
                })
                .catch(err => {
                    if (this.onError) this.onError(err as Error);
                    URL.revokeObjectURL(previewUrl);
                    this.entries.splice(this.entries.indexOf(entry), 1);
                })
                .finally(() => {
                    entry.setUploading(false);
                    this.promises = this.promises.filter(p => p !== promise);
                });
            this.promises.push(promise);
        }
    }

    removeFileAt(index: number) {
        const entry = this.entries[index];
        if (!entry) return;
        this.entries.splice(index, 1);
        if (entry.file && entry.previewUrl) {
            URL.revokeObjectURL(entry.previewUrl);
        }
        if (entry.meta && !entry.isExisting) {
            const path = (entry.meta as any).path;
            if (path) removeFile(path, this.storageService, this.fileProvider, this.tenantId).catch(() => undefined);
        }
    }

    async waitAll() {
        await Promise.all(this.promises);
    }

    clear() {
        for (const entry of [...this.entries]) {
            if (!entry.isExisting) {
                if (entry.meta) {
                    const path = (entry.meta as any).path;
                    if (path) {
                        removeFile(path, this.storageService, this.fileProvider, this.tenantId).catch(() => undefined);
                    }
                }
            }
        }
        this.entries = this.entries.filter(e => e.isExisting);
        this.promises = [];
    }

    destroy() {
        for (const entry of [...this.entries]) {
            if (entry.file && entry.previewUrl) {
                URL.revokeObjectURL(entry.previewUrl);
            }
        }
    }
}
